import locale from "@bonliva-crm/locale";
import { QualityDocumentOrderType } from "@bonliva-crm/shared";
import { Box, FormControlLabel, Radio, RadioGroup, Typography, makeStyles } from "@material-ui/core";
import { useMemo, useState } from "react";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantOrderQualityDocuments"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
};

export const useConsultantOrderQualityDocumentsAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { valueParser, selectedRows, entitySingular, entityPlural } = props;

  const [selectedType, setSelectedType] = useState<QualityDocumentOrderType | null>(null);

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  const { orderQualityDocuments } = useBulkActions({});

  const actionContent = (
    <Box className={classes.actionContent}>
      <Typography variant="body1" style={{ marginBottom: "0.5rem" }}>
        {locale.bulkActionSteps.action.ConsultantOrderQualityDocuments}
      </Typography>
      <RadioGroup
        value={selectedType ?? ""}
        onChange={(e) => setSelectedType(e.target.value as QualityDocumentOrderType)}
      >
        {Object.values(QualityDocumentOrderType).map((type) => (
          <FormControlLabel
            key={type}
            value={type}
            control={<Radio color="primary" />}
            label={locale.qualityDocumentOrderType[type]}
          />
        ))}
      </RadioGroup>
    </Box>
  );

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      <Typography variant="h4">
        {locale.bulkActionConfirmation(
          `beställa ${selectedType ? locale.qualityDocumentOrderType[selectedType] : ""} för`,
          parsedValues.nodeIds.length,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="h5">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const onSubmit = () => {
    if (!selectedType) return;
    orderQualityDocuments({
      consultantIds: parsedValues.nodeIds,
      qualityDocumentType: selectedType
    });
  };

  const reset = () => {
    setSelectedType(null);
  };

  const nextStepDisabled = selectedType === null;

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  actionContent: {
    display: "flex",
    flexDirection: "column",
    padding: "1rem 0"
  },
  confirmationContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "1rem"
  }
}));
